import { LoadingStageModel } from '@kts-front/models';
import { action, makeObservable, observable, runInAction } from 'mobx';

import { ENDPOINTS } from 'configs/endpoints';
import { LotModel } from 'entities/Lot/LotModel';
import { AddFavoriteLotRequest, FavoriteLotResult } from 'entities/Lot/types';
import { logApiErrWithSentry } from 'scripts/initSentry';
import { apiStore } from 'stores/global/ApiStore';
import { CommonApiError } from 'types/ApiError';

import { NotificationsStore } from '../NotificationsStore';

type FavoritesStoreOptions = {
  notificationsStore: NotificationsStore;
};

export class FavoritesStore {
  private readonly _notificationsStore: FavoritesStoreOptions['notificationsStore'];

  private readonly _lotLoadingStages = observable.map<number, LoadingStageModel>();

  constructor({ notificationsStore }: FavoritesStoreOptions) {
    this._notificationsStore = notificationsStore;

    makeObservable(this, {
      toggleLot: action.bound,
    });
  }

  getLotLoadingStage(lotId: number): LoadingStageModel | undefined {
    return this._lotLoadingStages.get(lotId);
  }

  async toggleLot(lot: LotModel, onChange?: (isFavorite: boolean) => void): Promise<boolean> {
    const loadingStage = this.getLotLoadingStage(lot.id) ?? new LoadingStageModel();

    if (loadingStage.isLoading) {
      return false;
    }

    const wasFavorite = lot.isFavorite.value;

    this._lotLoadingStages.set(lot.id, loadingStage);
    loadingStage.loading();
    let isSuccess = false;

    try {
      lot.isFavorite.change(!wasFavorite);
      onChange?.(!wasFavorite);
      isSuccess = wasFavorite ? await this.removeLot(lot.id) : await this.addLot(lot.id);

      return isSuccess;
    } finally {
      runInAction(() => {
        if (!isSuccess) {
          lot.isFavorite.change(wasFavorite);
          onChange?.(wasFavorite);
          loadingStage.error();
        } else {
          loadingStage.success();
        }
      });
    }
  }

  private async addLot(lotId: number): Promise<boolean> {
    const requestPayload: AddFavoriteLotRequest = { lot_id: lotId };
    const request = apiStore.createRequest({
      url: ENDPOINTS.favorites.create,
      method: 'POST',
    });

    const response = await request.call<FavoriteLotResult, CommonApiError>({ data: requestPayload });

    const alreadyAdded = response.isError && response.data?.status === 409;

    if (response.isError && !alreadyAdded) {
      this._notificationsStore.showErrorByResponse(response);
      logApiErrWithSentry({ endpoint: ENDPOINTS.favorites.create, method: 'POST', requestPayload, response });

      return false;
    }

    return true;
  }

  private async removeLot(lotId: number): Promise<boolean> {
    const request = apiStore.createRequest({
      url: ENDPOINTS.favorites.delete(lotId),
      method: 'DELETE',
    });
    const response = await request.call<void, CommonApiError>();

    if (response.isError) {
      this._notificationsStore.showErrorByResponse(response);
      logApiErrWithSentry({
        endpoint: ENDPOINTS.favorites.delete(lotId),
        method: 'DELETE',
        requestPayload: { lot_id: lotId },
        response,
      });

      return false;
    }

    return true;
  }
}
